import { DATE_NOT_TIMEZONE_FORMAT } from "@/constants/datetime";
import { dayjs } from "@/utils/dayjs";
import { TCompanyForm } from "@/features/company/types/companyForm";
import { TUpdateCompanyUserForm } from "@/features/company/types/updateCompanyUserForm";
import { TCreateCompanyReqDto } from "@/api/services/admin/company/dtos/create-company.dto";
import { TUpdateCompanyReqDto } from "@/api/services/admin/company/dtos/update-company.dto";
import { TGetCompanyDto } from "@/api/services/admin/company/dtos/get-company.dto";
import { TGetCompanyUserDto } from "@/api/services/admin/company/user/dtos/get-user.dto";

export const convertCompanyFormReq = (data: TCompanyForm): TCreateCompanyReqDto => {
  return {
    companyName: data.companyName,
    companyKanaName: data.companyKanaName ?? null,
    companySpecificIndustryId1: Number(data.companySpecificIndustryId1),
    companySpecificIndustryId2: data.companySpecificIndustryId2 ? Number(data.companySpecificIndustryId2) : null,
    companySpecificIndustryId3: data.companySpecificIndustryId3 ? Number(data.companySpecificIndustryId3) : null,
    companySpecificBusinessClassificationIds1: (data.companySpecificBusinessClassificationIds1 ?? []).map(Number),
    companySpecificBusinessClassificationIds2: (data.companySpecificBusinessClassificationIds2 ?? []).map(Number),
    companySpecificBusinessClassificationIds3: (data.companySpecificBusinessClassificationIds3 ?? []).map(Number),
    contractStatus: data.contractStatus,
    contractPlan: data.contractPlan ?? null,
    note: data.note ?? null,
    referralCompanyInfo: data.referralCompanyInfo ?? null,
    trialStartDate: data.trialStartDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    trialEndDate: data.trialEndDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    contractStartDate: data.contractStartDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    contractEndDate: data.contractEndDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    webAccessEnabled: data.webAccessEnabled ?? false,
    hiddenIndustryIds: data.hiddenIndustryIds ?? [],
  };
};

export const convertCompanyFormToUpdateReq = (data: TCompanyForm): TUpdateCompanyReqDto => {
  return {
    companyName: data.companyName,
    companyKanaName: data.companyKanaName ?? null,
    companySpecificIndustryId1: Number(data.companySpecificIndustryId1),
    companySpecificIndustryId2: data.companySpecificIndustryId2 ? Number(data.companySpecificIndustryId2) : null,
    companySpecificIndustryId3: data.companySpecificIndustryId3 ? Number(data.companySpecificIndustryId3) : null,
    companySpecificBusinessClassificationIds1: (data.companySpecificBusinessClassificationIds1 ?? []).map(Number),
    companySpecificBusinessClassificationIds2: (data.companySpecificBusinessClassificationIds2 ?? []).map(Number),
    companySpecificBusinessClassificationIds3: (data.companySpecificBusinessClassificationIds3 ?? []).map(Number),
    contractStatus: data.contractStatus,
    contractPlan: data.contractPlan ?? null,
    webAccessEnabled: data.webAccessEnabled ?? false,
    note: data.note ?? null,
    referralCompanyInfo: data.referralCompanyInfo ?? null,
    trialStartDate: data.trialStartDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    trialEndDate: data.trialEndDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    contractStartDate: data.contractStartDate ? data.contractStartDate.format(DATE_NOT_TIMEZONE_FORMAT) : null,
    contractEndDate: data.contractEndDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    hiddenIndustryIds: data.hiddenIndustryIds ?? [],
  };
};

export const convertCompanyResToForm = (data: TGetCompanyDto): Partial<TCompanyForm> => {
  return {
    companyName: data.companyName,
    companyKanaName: data.companyKanaName ?? null,
    // Coerce to Number on load so edit-mode defaults match the numeric option values (otherwise the
    // cross-row dedup filter, which compares strictly, would let an already-selected industry repeat).
    companySpecificIndustryId1: data.companySpecificIndustryId1 != null ? Number(data.companySpecificIndustryId1) : undefined,
    companySpecificIndustryId2: data.companySpecificIndustryId2 != null ? Number(data.companySpecificIndustryId2) : null,
    companySpecificIndustryId3: data.companySpecificIndustryId3 != null ? Number(data.companySpecificIndustryId3) : null,
    companySpecificBusinessClassificationIds1: (data.companySpecificBusinessClassificationIds1 ?? []).map(String),
    companySpecificBusinessClassificationIds2: (data.companySpecificBusinessClassificationIds2 ?? []).map(String),
    companySpecificBusinessClassificationIds3: (data.companySpecificBusinessClassificationIds3 ?? []).map(String),
    companySpecificIndustry1: data.companySpecificIndustry1 ?? null,
    companySpecificIndustry2: data.companySpecificIndustry2 ?? null,
    companySpecificIndustry3: data.companySpecificIndustry3 ?? null,
    hiddenIndustryIds: data.hiddenIndustryIds ?? [],
    contractStatus: data.contractStatus ?? "",
    contractPlan: data.contractPlan ?? null,
    webAccessEnabled: data.webAccessEnabled ?? false,
    referralCompanyInfo: data.referralCompanyInfo ?? null,
    note: data.note ?? null,
    trialStartDate: data.trialStartDate ? dayjs(data.trialStartDate) : null,
    trialEndDate: data.trialEndDate ? dayjs(data.trialEndDate) : null,
    contractEndDate: data.contractEndDate ? dayjs(data.contractEndDate) : null,
    contractStartDate: data.contractStartDate ? dayjs(data.contractStartDate) : null,
  };
};

export const convertCompanyUserResToForm = (data: TGetCompanyUserDto): Partial<TUpdateCompanyUserForm> => {
  return {
    email: data.email,
    fullName: data.fullName,
    isUser: Number(data.isUser) ?? 0,
    activeStatus: data.activeStatus ?? null,
  };
};
